import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
    Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Sector
} from 'recharts';
import CustomLegendIcon from './legendicon';
import { Typography } from '@material-ui/core'
const COLOR_PATTERNS = ['#4285F4', '#D65140', '#8254A9', '#34A853', '#F6B646', '#284C88'];
const useStyles = makeStyles(() => ({
    root: {
        position: 'relative',
    },
}));

const Chart = props => {
    const { loading, data, title, className, innerRadius, ...rest } = props;
    const [activeIndex, setActiveIndex] = useState(0)
    const classes = useStyles();

    const dataProp = data.map((item, index) => ({
        name: item.label,
        value: item.value,
        fill: COLOR_PATTERNS[index % COLOR_PATTERNS.length],
    }))
    const legendList = data.map((item, index) => ({
        value: item.label,
        color: COLOR_PATTERNS[index % COLOR_PATTERNS.length],
        legendIcon: < CustomLegendIcon fill={COLOR_PATTERNS[index % COLOR_PATTERNS.length]} />
    }))
    const content = (props) => {
        const data = dataProp[props.label]
        if (data) {
            const text = `${dataProp[props.label].name}: ${dataProp[props.label].value}`;
            return (
                <Typography
                    style={{
                        backgroundColor: 'white',
                        padding: 10,
                        borderRadius: 4,
                        boxShadow: '0px 8px 35px rgba(6, 56, 63, 0.1)',
                        maxWidth: 200,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                    }}
                >
                    {text}
                </Typography>
            )
        }
        return ''
    }
    return (
        <div
            {...rest}
            className={clsx(classes.root, className)}
        >
            <Typography variant='h6' style={{ textAlign: 'center' }}><strong>{title}</strong></Typography>
            {
                !loading &&
                <ResponsiveContainer>
                    <PieChart width={400} height={400}>
                        <Pie
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            onMouseEnter={(_, index) => {
                                setActiveIndex(index)
                            }}
                        >
                            {
                                data.map((entry, index) => <Cell key={dataProp[index].name} fill={COLOR_PATTERNS[index % COLOR_PATTERNS.length]} />)
                            }
                        </Pie>
                        <Tooltip data content={content} payload={legendList} />
                    </PieChart>
                </ResponsiveContainer>
            }
        </div>
    );
};

Chart.propTypes = {
    className: PropTypes.string,
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    title: PropTypes.any,
    innerRadius: PropTypes.any,
    label: PropTypes.any
};
function areEqual(prevProps, nextProps) {
        return !(prevProps.data != nextProps.data || prevProps.title != nextProps.title)
}
export default React.memo(Chart, areEqual);
const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value, label } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 35) * cos;
    const my = cy + (outerRadius + 35) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const isSmall = (window?.innerWidth < 600);
    let textAnchor;
    if (isSmall) {
        textAnchor = 'middle';
    } else {
        textAnchor = cos >= 0 ? 'start' : 'end';
    }
    let labelX;
    if (isSmall) {
        labelX = mx;
    } else {
        const direction = cos >= 0 ? 1 : -1;
        labelX = ex + direction * 4;
    }

    // helper to split long strings into multiple lines
    const splitToLines = (str, maxLen = 18) => {
        if (!str) return [''];
        if (str.length <= maxLen) return [str];
        const words = str.split(' ');
        const lines = [];
        let cur = '';
        words.forEach(w => {
            if ((cur + ' ' + w).trim().length <= maxLen) cur = (cur + ' ' + w).trim();
            else {
                if (cur) lines.push(cur);
                cur = w;
            }
        })
        if (cur) lines.push(cur);
        return lines;
    }

    return (
        <g>
            <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
                {payload.name}
            </text>
            <Sector
                cx={cx}
                cy={cy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
            <Sector
                cx={cx}
                cy={cy}
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={outerRadius + 6}
                outerRadius={outerRadius + 10}
                fill={fill}
            />
            {!isSmall && <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />}
            {!isSmall && <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />}
                        {
                            // render the label/value on multiple lines when necessary
                        }
                        {
                            (() => {
                                const full = `${label}: ${value}`;
                                // use a slightly smaller max length on narrow screens
                                const parts = splitToLines(full, isSmall ? 8 : 18);
                                return (
                                    <text x={labelX} y={(isSmall ? my : ey) + (parts.length > 1 ? -6 : 3)} textAnchor={textAnchor} fill={fill} fontSize="0.7em">
                                        {parts.map((p, i) => (
                                            <tspan key={label + i} x={labelX} dy={i === 0 ? 0 : 12}>
                                                {p}
                                            </tspan>
                                        ))}
                                    </text>
                                )
                            })()
                        }
        </g>
    );
};